/*
 * PASS-943 Modal components taken from @rally/connect-components (a.k.a telemedicine-components)
 * adjusted for RDS-based styling
 */
import React, { Component, HTMLAttributes } from 'react';
import ReactModal from 'react-modal';
import styled, { css } from 'styled-components';
// Used for mobile users to prevent background/body scrolling when trying to scroll modal content

// FUS-2242 and FIS-118 Enable animation workaround for all mobile browsers, not just Safari
import { Text } from '@pass-frontend/common/components/typography/ui';
import { isMobileView } from '@pass-frontend/utility/style-helpers';

export const modalContentCssClass = '.ReactModal__Content';
export const modalOverlayCssClass = '.ReactModal__Overlay';

export const animations = css`
  @keyframes modal-slide-up {
    0% {
      transform: translateY(100vh);
    }
    100% {
      transform: translateY(0);
    }
  }

  @keyframes modal-slide-down {
    0% {
      transform: translateY(0);
    }
    100% {
      transform: translateY(100vh);
    }
  }
`;

type ModalContainerHTMLAttributes = HTMLAttributes<HTMLElement>;
export interface Props extends ModalContainerHTMLAttributes {
  isOpen?: boolean;
  shouldCloseOnOverlayClick?: boolean;
  shouldCloseOnEsc?: boolean;
  closed?: () => void;
  onAfterOpen?: () => void;
  onRequestedClose?: () => void;
  clickedActionButton?: (e: MouseEvent) => any | Promise<any>;
  clickedSecondaryButton?: (e: MouseEvent) => any | Promise<any>;
  animationOutTimeMs?: number;
  animationInTimeMs?: number;
  overlayAnimationTimeMs?: number;
  loading?: boolean;
  ariaHeadingId: string;
  role?: string;
  overlayZIndex?: number; // added this prop since some modals are full screen desktop and need to override z-index
}

export const setAppElementForBaseModal = (appElement: HTMLElement | string) => {
  ReactModal.setAppElement(appElement);
};

interface State {
  isOpen: boolean;
  loading: boolean;
}
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
function ReactModalAdapter({
  className,
  modalClassName,
  isOpen,
  overlayClassName,
  shouldCloseOnOverlayClick,
  ...props
}) {
  return (
    <ReactModal
      className={modalClassName}
      portalClassName={`${className} portal`}
      isOpen={isOpen}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      overlayClassName={overlayClassName}
      {...(props as ReactModal.Props)}
    />
  );
}

const StyledModal = styled(ReactModalAdapter).attrs({
  overlayClassName: 'overlay',
})`
  @keyframes modal-slide-up {
    0% {
      transform: translateY(100vh);
    }
    100% {
      transform: translateY(0);
    }
  }

  @keyframes modal-slide-down {
    0% {
      transform: translateY(0);
    }
    100% {
      transform: translateY(100vh);
    }
  }

  .overlay {
    align-items: center;
    background: rgba(0, 0, 0, 0.2);
    box-sizing: border-box;
    display: flex;
    height: 100vh;
    /* mobile viewport bug fix */
    height: -webkit-fill-available;
    min-height: -webkit-fill-available;
    justify-content: center;
    left: 0;
    overflow: hidden;
    position: fixed;
    top: 0;
    transition-property: opacity;
    transition-timing-function: ease-in-out;
    width: 100vw;
    z-index: ${(props) => props.overlayZIndex || 10};

    &.ReactModal__Overlay--after-open {
      ${({ overlayAnimationTimeMs, animationInTimeMs }) => `
        transition-duration: ${
          overlayAnimationTimeMs
            ? `${overlayAnimationTimeMs + animationInTimeMs}ms`
            : '0'
        };
      `}
      opacity: 1;
    }

    &.ReactModal__Overlay--before-close {
      ${({ overlayAnimationTimeMs, animationOutTimeMs }) => `
        transition-duration: ${
          overlayAnimationTimeMs
            ? `${overlayAnimationTimeMs + animationOutTimeMs}ms`
            : '0'
        };
      `}
      opacity: 0;
    }
  }

  .ReactModal__Content {
    animation-direction: normal;
    animation-duration: 0ms;
    animation-fill-mode: forwards;
    animation-timing-function: cubic-bezier(0.23, 1, 0.32, 1);
    ${isMobileView() && 'animation-delay: 5ms;'}
  }

  ${({ animationInTimeMs, animationOutTimeMs }) => `
    .ReactModal__Content--after-open {
      animation-duration: ${animationInTimeMs || 0}ms;
      animation-name: modal-slide-up;
    }

    .ReactModal__Content--before-close {
      animation-duration: ${animationOutTimeMs || 0}ms;
      animation-name: modal-slide-down;
    }
  `}

  ${isMobileView() &&
  `
    .ReactModal__Content--after-open-completed {
        top: 0;
    }
  `}

  /* TODO may need to remove this for A11y */
  .ReactModal__Content {
    &:focus {
      border: none;
      outline: none;
    }
  }
`;

const ModalWithStyledText = (props: ModalContainerHTMLAttributes) => (
  <Text tag="aside" {...props} />
);
export const ModalContainer = styled(ModalWithStyledText)``;

export class BaseModal extends Component<Props, State> {
  closeButtons?: Element[] = [];
  element?: HTMLDivElement;
  actionButton?: HTMLElement;
  nextActionButton?: HTMLElement;

  constructor(props: Props) {
    super(props);
    this.state = {
      loading: props.loading || false,
      isOpen: props.isOpen || false,
    };
  }

  componentDidUpdate(oldProps: Props) {
    const { isOpen, loading } = this.props;
    if (oldProps.isOpen !== isOpen) {
      if (isOpen) {
        this.setState({ isOpen: true });
      } else {
        this.closed();
      }
    }
    if (oldProps.loading !== loading) {
      this.setState({ loading });
    }
  }

  componentWillUnmount() {
    if (this.element) {
      this.element.removeEventListener(
        'animationstart',
        this.handleAnimationStart,
      );
      this.element.removeEventListener('animationend', this.handleAnimationEnd);
    }
  }

  /**
   * TODO Do we need to bind using addEventListener instead of the conventional React synthetic events? Code is
   * mostly copied from telemedicine-components
   */
  bindEvents = () => {
    //noticed that we bind modal actions by specific classes, thus when styledcomponent hijacks events passed through props
    //this is how it knows what click to register to which action

    this.closeButtons = Array.from(
      this.element?.getElementsByClassName('close-btn'),
    );

    this.nextActionButton = this.element.querySelector('.next-action-btn');
    if (this.nextActionButton) {
      this.nextActionButton.addEventListener('click', this.nextAction);
    }
    this.onRequestedClose = this.onRequestedClose.bind(this);
    for (const button of this.closeButtons) {
      button.addEventListener('click', this.onRequestedClose);
    }
    this.actionButton = this.element?.querySelector('.action-btn');
    if (this.actionButton) {
      this.actionButton.addEventListener('click', this.action);
    }
    // toggle modal completed class for safari animation start/end as a fallback in case modal gets stuck at the top
    this.element?.addEventListener('animationstart', this.handleAnimationStart);
    this.element?.addEventListener('animationend', this.handleAnimationEnd);
  };

  handleAnimationStart = () => {
    if (this.element?.classList.contains('ReactModal__Content--after-open')) {
      this.element.classList.add('ReactModal__Content--after-open-completed');
    }
  };

  handleAnimationEnd = () => {
    if (this.element?.classList.contains('ReactModal__Content--after-open')) {
      this.element.classList.remove(
        'ReactModal__Content--after-open-completed',
      );
    }
  };

  onRequestedClose = () => {
    const { onRequestedClose } = this.props;
    if (onRequestedClose) {
      onRequestedClose();
    }
  };

  onAfterOpen = () => {
    this.bindEvents();
    const { onAfterOpen } = this.props;
    if (onAfterOpen) {
      onAfterOpen();
    }
  };

  closed = () => {
    this.setState({ isOpen: false });
    const { closed } = this.props;
    if (closed) {
      closed();
    }
  };

  action = async (e: MouseEvent) => {
    const { clickedActionButton } = this.props;
    if (!clickedActionButton) {
      return;
    }
    this.setState({ loading: true });
    const pending = await clickedActionButton(e);
    if (!this.state.isOpen) {
      return;
    }
    if (pending === true) {
      this.closed();
    }
    this.setState({ loading: false });
  };

  nextAction = async (e: MouseEvent) => {
    const { clickedSecondaryButton } = this.props;
    clickedSecondaryButton?.(e);
  };

  render() {
    const {
      children,
      shouldCloseOnOverlayClick,
      className,
      shouldCloseOnEsc,
      animationInTimeMs,
      animationOutTimeMs,
      overlayAnimationTimeMs,
      ariaHeadingId,
      role,
      overlayZIndex,
      ...containerHTMLAttributes
    } = this.props;
    const { isOpen, loading } = this.state;
    delete containerHTMLAttributes.isOpen;
    delete containerHTMLAttributes.closed;
    delete containerHTMLAttributes.onAfterOpen;
    delete containerHTMLAttributes.clickedActionButton;
    delete containerHTMLAttributes.clickedSecondaryButton;
    delete containerHTMLAttributes.loading;
    delete containerHTMLAttributes.onRequestedClose;
    const sanitizedAnimationInTime =
      typeof animationInTimeMs === 'number' ? animationInTimeMs : 600;
    const sanitizedAnimationOutTime =
      typeof animationOutTimeMs === 'number' ? animationOutTimeMs : 300;
    const sanitizedOverlayAnimationTime =
      typeof overlayAnimationTimeMs === 'number' ? overlayAnimationTimeMs : 20;

    return (
      <StyledModal
        aria={{ labelledby: ariaHeadingId, modal: true }}
        modalClassName={`modal ${className || ''}`}
        isOpen={isOpen}
        onRequestClose={this.onRequestedClose}
        onAfterOpen={this.onAfterOpen}
        contentRef={(node: HTMLDivElement) => (this.element = node)}
        shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
        shouldCloseOnEsc={shouldCloseOnEsc}
        animationInTimeMs={sanitizedAnimationInTime}
        animationOutTimeMs={sanitizedAnimationOutTime}
        closeTimeoutMS={
          sanitizedAnimationOutTime + sanitizedOverlayAnimationTime
        }
        overlayAnimationTimeMs={sanitizedOverlayAnimationTime}
        role={role}
        ariaHideApp={false} // https://stackoverflow.com/questions/48269381/warning-react-modal-app-element-is-not-defined-please-use-modal-setappeleme
        overlayZIndex={overlayZIndex}
      >
        <ModalContainer {...containerHTMLAttributes}>{children}</ModalContainer>
      </StyledModal>
    );
  }
}
